import { useQueryClient } from '@tanstack/react-query';
import { get } from 'lodash';
import {
  FC,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { queryKeys } from 'src/constants/querykeys';
import { fetchProductDetailsQuery } from 'src/services/catalog_products/productDetailsQueryFn';
import useGetProductDetailsQuery from 'src/services/catalog_products/useGetProductDetailsQuery';
import { RecommendPageParams } from 'src/types/recommendation_items';
import { createContext } from 'use-context-selector';
import { isCancelledQueryError } from './searchProductContext.guards';
import { SearchProductDetailsContextShape } from './searchProductContext.types';
import { SearchResultProduct } from './searchProductData';

export const SearchProductDetailsContext = createContext<SearchProductDetailsContextShape>(
  {} as SearchProductDetailsContextShape
);

export const SearchProductDetailsProvider: FC<PropsWithChildren> = ({ children }): ReactElement => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isProductPanelOpen, setIsProductPanelOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SearchResultProduct | null>(null);
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState(false);
  const isFetchingProductDetailsRef = useRef(false);
  const [isSwapItemModalOpen, setIsSwapItemModalOpen] = useState(false);
  const [swapItemData, setSwapItemData] = useState<RecommendPageParams | null>(null);

  const {
    data: productDetails,
    isLoading: isLoadingProductDetails,
    isFetching: fetchingProductDetails,
    refetch: fetchProductDetails,
  } = useGetProductDetailsQuery(
    get(selectedProduct, 'productId', null),
    get(selectedProduct, 'catalogId', null),
    get(selectedProduct, 'categoryId', null)
  );

  const loadingProductDetails = isLoadingProductDetails || fetchingProductDetails;

  const handleViewProductDetails = useCallback(
    async (e: MouseEvent, product: SearchResultProduct): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();

      if (isFetchingProductDetailsRef.current || loadingProductDetails) {
        return;
      }

      try {
        isFetchingProductDetailsRef.current = true;
        setIsFetchingProductDetails(true);

        await queryClient.cancelQueries({
          queryKey: [...queryKeys.product.root, 'productDetails'],
        });

        setSelectedProduct(product);

        await queryClient.fetchQuery({
          queryKey: queryKeys.product.productDetails(
            product.productId,
            product.catalogId,
            product.categoryId
          ),
          queryFn: fetchProductDetailsQuery,
        });

        setIsProductPanelOpen(true);
      } catch (error) {
        if (!isCancelledQueryError(error)) {
          toast.error(t('productDetailsLoadFailed'));
        }
      } finally {
        isFetchingProductDetailsRef.current = false;
        setIsFetchingProductDetails(false);
      }
    },
    [loadingProductDetails, queryClient, t]
  );

  const handleCloseProductPanel = useCallback(() => {
    setIsProductPanelOpen(false);
    setSelectedProduct(null);
  }, []);

  const getSelectedProductCatalogType = useCallback(
    () => selectedProduct?.catalogType,
    [selectedProduct]
  );

  const value = useMemo<SearchProductDetailsContextShape>(
    () => ({
      selectedProduct,
      setSelectedProduct,
      productDetails,
      loadingProductDetails,
      isFetchingProductDetails,
      isProductPanelOpen,
      handleCloseProductPanel,
      handleViewProductDetails,
      fetchProductDetails,
      getSelectedProductCatalogType,
      isSwapItemModalOpen,
      setIsSwapItemModalOpen,
      swapItemData,
      setSwapItemData,
    }),
    [
      selectedProduct,
      productDetails,
      loadingProductDetails,
      isFetchingProductDetails,
      isProductPanelOpen,
      handleCloseProductPanel,
      handleViewProductDetails,
      fetchProductDetails,
      getSelectedProductCatalogType,
      isSwapItemModalOpen,
      swapItemData,
    ]
  );

  return (
    <SearchProductDetailsContext.Provider value={value}>
      {children}
    </SearchProductDetailsContext.Provider>
  );
};
